import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { EditorComposer, Editor, Divider } from '../../editor';
import ToolbarPlugin from '../../editor/plugins/ToolbarPlugin/ToolbarPlugin';
import {
  AlignDropdown,
  BackgroundColorPicker,
  BlockFormatDropdown,
  BoldButton,
  CodeFormatButton,
  CodeLanguageDropdown,
  FloatingLinkEditor,
  FontFamilyDropdown,
  FontSizeDropdown,
  InsertDropdown,
  InsertLinkButton,
  ItalicButton,
  RedoButton,
  TextColorPicker,
  TextFormatDropdown,
  UnderlineButton,
  UndoButton,
} from '../../editor/plugins/ToolbarPlugin/components';
import { $createParagraphNode, $createTextNode, $getRoot } from 'lexical';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $createEquationNode } from '../../editor/nodes/EquationNode';
import { $createImageNode } from '../../editor/nodes/ImageNode';
import { useAuth } from '../../context/AuthContext';
import { createEditor } from 'lexical';
import PlaygroundNodes from '../../editor/nodes/PlaygroundNodes';
import { processMathEquation, splitTextAndMath } from '../utils/latexParser';
import { Modal, Button } from '../common';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';

// EditorComponent 수정
function EditorComponent({ onLoad, onSave }) {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    // ContentEditable__root 요소를 찾아서 스타일 적용
    setTimeout(() => {
      const editorElement = document.querySelector('div.ContentEditable__root');
      if (editorElement) {
        // 기본 CSS 오버라이드 - 컨텐츠에 맞게 높이 자동 조절
        editorElement.style.padding = '20px';
        editorElement.style.minHeight = '200px';
        editorElement.style.height = 'auto';
        editorElement.style.overflow = 'visible';
        editorElement.style.resize = 'none';
        editorElement.style.maxWidth = 'none';
        editorElement.style.width = '100%';
        editorElement.style.whiteSpace = 'pre-wrap';
        editorElement.style.fontSize = '16px';  // 폰트 크기 줄임
        editorElement.style.fontWeight = '500';  // 문제 미리보기와 동일한 폰트 굵기
      }
      
      // editor-container 스타일 설정
      const editorInnerContainer = document.querySelector('.editor-container');
      if (editorInnerContainer) {
        editorInnerContainer.style.width = '100%';
        editorInnerContainer.style.maxWidth = '465px';
        editorInnerContainer.style.margin = '0 auto';
        editorInnerContainer.style.overflow = 'visible';
        editorInnerContainer.style.height = 'auto';
        editorInnerContainer.style.minHeight = 'auto';
      }
      
      // editor-shell 스타일 오버라이드
      const editorShell = document.querySelector('.editor-shell');
      if (editorShell) {
        editorShell.style.maxWidth = 'none';
        editorShell.style.width = '100%';
        editorShell.style.margin = '0';
        editorShell.style.height = 'auto';
      }
    }, 100);
  }, []);

  const handleMathData = useCallback(async (data) => {
    try {
      console.log("Received data in handleMathData:", data);
      
      editor.update(() => {
        const root = $getRoot();
        root.clear();
        
        let currentParagraph = $createParagraphNode();
        root.append(currentParagraph);

        const linedData = data.mathpix_data?.lined_data;
        if (!linedData) {
          console.error('No lined_data found in:', data);
          return;
        }

        linedData.forEach(item => {
          if ((item.type !== 'text' && item.type !== 'math' && item.type !== 'table') || 
              item.type === 'chart' || 
              item.type === 'diagram') {
            if (item.content && item.content[0] && 
                (item.content[0].startsWith('data:image') || item.is_base64)) {
              const imageNode = $createImageNode({
                altText: 'Solution Image',
                src: item.content[0],
                width: item.cnt ? Math.abs(item.cnt[2][0] - item.cnt[0][0]) : undefined,
                height: item.cnt ? Math.abs(item.cnt[2][1] - item.cnt[0][1]) : undefined,
                maxWidth: 500,
                showCaption: false
              });
              currentParagraph.append(imageNode);
              
              currentParagraph = $createParagraphNode();
              root.append(currentParagraph);
            }
          } else if (item.type === 'text') {
            item.content.forEach(text => {
              const mathSegments = splitTextAndMath(text);
              mathSegments.forEach(mathSegment => {
                if (mathSegment.type === 'text') {
                  const textNode = $createTextNode(mathSegment.content);
                  currentParagraph.append(textNode);
                } else {
                  const equation = processMathEquation(mathSegment.content);
                  const equationNode = $createEquationNode(equation, true);
                  currentParagraph.append(equationNode);
                }
              });
            });
            
            currentParagraph = $createParagraphNode();
            root.append(currentParagraph);
          } else if (item.type === 'math' || item.type === 'table') {
            const mathContent = item.content[0];
            let equation = mathContent.replace(/^\$\$(.*)\$\$/s, '$1');
            
            if (item.type === 'table') {
              equation = equation
                .replace(/\\begin\{tabular\}/g, '\\begin{array}')
                .replace(/\\end\{tabular\}/g, '\\end{array}')
                .replace(/\$/g, ' ')
                .replace(/\\hline(?!\s)/g, '\\hline ');
            }
            
            equation = processMathEquation(equation);
            const isDisplayMath = mathContent.startsWith('$$');
            const equationNode = $createEquationNode(equation, !isDisplayMath);
            currentParagraph.append(equationNode);
            
            currentParagraph = $createParagraphNode();
            root.append(currentParagraph);
          }
        });
      });
    } catch (error) {
      console.error('Math data handling error:', error);
      console.error('Error details:', error.message);
    }
  }, [editor]);

  useEffect(() => {
    if (onLoad) {
      onLoad(handleMathData);
    }
  }, [onLoad, handleMathData]);

  const saveEditorState = useCallback(() => {
    const editorState = editor.getEditorState();
    const json = editorState.toJSON();
    onSave(json);
  }, [editor, onSave]);

  return (
    <div className="flex flex-col">
      <div className="editor-wrapper">
        <Editor>
          <ToolbarPlugin>
            {/* <FontFamilyDropdown /> */}
            {/* <FontSizeDropdown /> */}
            {/* <Divider /> */}
            {/* <BoldButton /> */}
            {/* <ItalicButton /> */}
            {/* <UnderlineButton /> */}
            {/* <CodeFormatButton /> */}
            {/* <InsertLinkButton /> */}
            {/* <TextColorPicker /> */}
            {/* <BackgroundColorPicker /> */}
            <TextFormatDropdown />
            <Divider />
            <InsertDropdown />
            <Divider />
            <AlignDropdown />
          </ToolbarPlugin>
        </Editor>
      </div>
      <div className="border-t py-2 px-2 flex justify-end items-center">
        <Button
          onClick={saveEditorState}
          variant="primary"
          className="bg-purple-500 hover:bg-purple-600"
          disabled={!editor}
        >
          풀이 저장
        </Button>
      </div>
    </div>
  );
}

function SolutionEditModal({ solution, onClose, onUpdate }) {
  const { getAccessToken } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [imageUrl, setImageUrl] = useState(solution.base64format);
  const [isLoading, setIsLoading] = useState(false);
  const canvasRef = useRef(null);
  const mathDataHandlerRef = useRef(null);
  const isMobileView = window.innerWidth < 768;
  const [imageScale, setImageScale] = useState(1);
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);

  const getInitialEditorState = useCallback(() => {
    const editor = createEditor({
      nodes: [...PlaygroundNodes],
      theme: {
        paragraph: 'editor-paragraph',
        text: {
          bold: 'editor-text-bold',
          italic: 'editor-text-italic',
          underline: 'editor-text-underline',
        },
      }
    });
    
    if (solution.lexical_data?.lexical_data && 
        typeof solution.lexical_data.lexical_data === 'object') {
      try {
        const lexicalData = JSON.parse(JSON.stringify(solution.lexical_data.lexical_data));
        
        if (!lexicalData.root) {
          return createEmptyEditorState(editor);
        }

        const processNode = (node) => {
          node.style = node.style || "";
          node.format = node.format || "";
          node.indent = node.indent || 0;
          node.direction = node.direction || null;
          node.version = node.version || 1;

          if (node.type === 'equation') {
            node.equation = processMathEquation(node.equation);
          }

          if (node.children && Array.isArray(node.children)) {
            node.children = node.children.map(child => processNode(child));
          }

          return node;
        };

        lexicalData.root = processNode(lexicalData.root);
        return editor.parseEditorState(lexicalData);
        
      } catch (error) {
        console.error('Error parsing editor state:', error);
        return createEmptyEditorState(editor);
      }
    }
    
    return createEmptyEditorState(editor);
  }, [solution]);

    // 빈 에디터 상태 생성 함수
    const createEmptyEditorState = (editor) => {
        return editor.parseEditorState({
          root: {
            children: [
              {
                children: [],
                direction: null,
                format: "",
                indent: 0,
                type: "paragraph",
                version: 1,
                style: ""
              }
            ],
            direction: null,
            format: "",
            indent: 0,
            type: "root",
            version: 1,
            style: ""
          }
        });
      };

  const drawBoundingBoxes = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || !imageUrl || !solution.mathpix_data) return;

    const img = new Image();
    img.src = imageUrl;
    
    img.onload = () => {
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d');
      
      ctx.drawImage(img, 0, 0, img.width, img.height);
      
      solution.mathpix_data.lined_data.forEach(item => {
        if (item.cnt && item.cnt.length === 4) {
          ctx.strokeStyle = 'red';
          ctx.lineWidth = 2;
          
          const [[x1, y1], [x2, y2], [x3, y3], [x4, y4]] = item.cnt;
          
          ctx.beginPath();
          ctx.moveTo(x1, y1);
          ctx.lineTo(x2, y2);
          ctx.lineTo(x3, y3);
          ctx.lineTo(x4, y4);
          ctx.closePath();
          ctx.stroke();
        }
      });
    };
  }, [imageUrl, solution.mathpix_data]);

  const fetchWithTokenRefresh = useCallback(async (url, options = {}) => {
    try {
      const token = await getAccessToken();
      if (!token) {
        throw new Error('인증 토큰이 없습니다.');
      }

      const response = await fetch(`${API_BASE_URL}${url}`, {
        ...options,
        headers: {
          ...options.headers,
          'Authorization': token,
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('API 요청 실패');
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('API 호출 에러:', error);
      throw error;
    }
  }, [getAccessToken]);

  const handleSave = async (lexicalJson) => {
    try {
      const response = await fetchWithTokenRefresh(`/api/solutions/${solution.solution_id}/lexical`, {
        method: 'POST',
        body: JSON.stringify({
          lexical_json: lexicalJson
        })
      });

      if (!response || (response.status_code !== 200 && response.status_code !== 201)) {
        throw new Error('에디터 데이터 저장 실패');
      }

      // onUpdate 콜백이 있으면 사용하고, 없으면 현재 페이지를 새로고침
      if (onUpdate) {
        onUpdate();
      } else {
        // 현재 페이지 새로고침 대신 navigate를 사용하여 상태 갱신
        navigate(location.pathname + location.search, { replace: true });
      }
      onClose();
      
      alert('풀이가 성공적으로 저장되었습니다.');
    } catch (error) {
      console.error('에디터 데이터 저장 중 오류:', error);
      alert('풀이 저장에 실패했습니다.');
    }
  };

  const handleOcr = useCallback(async () => {
    if (!solution.solution_id || !mathDataHandlerRef.current) return;

    setIsLoading(true);
    try {
      const response = await fetchWithTokenRefresh(`/api/solutions/${solution.solution_id}/mathpix`, {
        method: 'POST',
        body: JSON.stringify({
          mathpix_data_option: true
        })
      });

      if (!response || response.status_code !== 201) {
        throw new Error('OCR 처리 실패');
      }

      drawBoundingBoxes(response.data);
      await mathDataHandlerRef.current(response.data);
      onUpdate();
      
      alert('OCR 처리가 완료되었습니다.');
    } catch (error) {
      console.error('OCR 처리 중 오류:', error);
      alert('OCR 처리에 실패했습니다.');
    } finally {
      setIsLoading(false);
    }
  }, [solution.solution_id, fetchWithTokenRefresh, drawBoundingBoxes, onUpdate]);

  const setMathDataHandler = useCallback((handler) => {
    mathDataHandlerRef.current = handler;
    if (solution.solution_info.status === 300 && solution.mathpix_data) {
      handler(solution.mathpix_data);
    }
  }, [solution]);

  const handleApplyOCR = useCallback(async () => {
    if (!solution.solution_id || !mathDataHandlerRef.current || !solution.mathpix_data) return;

    setIsLoading(true);
    try {
      await mathDataHandlerRef.current(solution);
      drawBoundingBoxes(solution);
      alert('OCR 데이터가 적용되었습니다.');
    } catch (error) {
      console.error('OCR 데이터 적용 중 오류:', error);
      alert('OCR 데이터 적용에 실패했습니다.');
    } finally {
      setIsLoading(false);
    }
  }, [solution, drawBoundingBoxes]);

  useEffect(() => {
    if (solution.solution_info.status >= 300) {
      drawBoundingBoxes();
    }
  }, [drawBoundingBoxes, solution.solution_info.status]);

  // 닫기 잘리 처리
  const handleCloseModal = useCallback(() => {
    setShowCloseConfirm(true);
  }, []);

  const confirmClose = useCallback(() => {
    setShowCloseConfirm(false);
    onClose();
  }, [onClose]);

  const cancelClose = useCallback(() => {
    setShowCloseConfirm(false);
  }, []);

  return (
    <>
    <Modal
      isOpen={true}
      onClose={handleCloseModal}
      title={
        <div className="flex items-baseline gap-2">
          <span>풀이 수정</span>
          <span className="text-sm text-gray-500">- SID: {solution.solution_id}</span>
        </div>
      }
      size="full"
      className="flex flex-col"
      bodyClassName="flex flex-col flex-1 overflow-auto p-4"
      zIndex={9999800}
    >
      <div className="flex flex-col">
        {/* 상단: 이미지 영역 */}
        <div className="mb-4">
          <div className="space-y-2">
            {/* 이미지 컨테이너 */}
            <div className="relative overflow-auto rounded-lg border border-gray-200 bg-gray-50">
              <div 
                className="relative inline-block min-w-full p-4"
                style={{ transform: `scale(${imageScale})`, transformOrigin: 'top left' }}
              >
                <img 
                  src={imageUrl} 
                  alt="Solution" 
                  className="size-auto max-w-full"
                />
                {solution.solution_info.status >= 300 && (
                  <canvas
                    ref={canvasRef}
                    className="pointer-events-none absolute left-0 top-0"
                  />
                )}
              </div>
            </div>
            
            {/* 확대/축소 컨트롤 */}
            <div className="flex items-center justify-center gap-2">
              <button
                onClick={() => setImageScale(Math.max(0.5, imageScale - 0.1))}
                className="rounded-full border border-gray-300 p-1 hover:bg-gray-100"
                title="축소"
              >
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
                </svg>
              </button>
              <span className="min-w-[60px] text-center text-sm font-medium">
                {Math.round(imageScale * 100)}%
              </span>
              <button
                onClick={() => setImageScale(Math.min(2, imageScale + 0.1))}
                className="rounded-full border border-gray-300 p-1 hover:bg-gray-100"
                title="확대"
              >
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
              </button>
              <button
                onClick={() => setImageScale(1)}
                className="ml-2 rounded-full border border-gray-300 px-3 py-1 text-sm hover:bg-gray-100"
                title="원본 크기"
              >
                원본
              </button>
            </div>
          </div>

          {solution.solution_info.status === 200 && (
            <Button
              onClick={handleOcr}
              variant="primary"
              className="mt-4"
              loading={isLoading}
            >
              {isLoading ? '처리 중...' : 'OCR 처리 시작'}
            </Button>
          )}

          {solution.solution_info.status === 300 && (
            <Button
              onClick={handleApplyOCR}
              variant="primary"
              className="mt-4 bg-green-500 hover:bg-green-600"
              loading={isLoading}
            >
              {isLoading ? '처리 중...' : 'OCR 데이터 적용'}
            </Button>
          )}
        </div>

        {/* 하단: 에디터 영역 */}
        <div className="mb-4">
          <div className="mb-3 flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-800">수식 편집기</h3>
            <div className="text-sm text-gray-500">
              풀이 내용을 편집하세요
            </div>
          </div>
          <div className="rounded-lg border border-gray-300 bg-white shadow-sm">
            <div className="w-full [&_.editor-container]:!font-inherit [&_div.editor-equation]:block [&_span.editor-equation]:inline-block">
              <EditorComposer initialEditorState={getInitialEditorState()}>
                <EditorComponent 
                  onLoad={setMathDataHandler} 
                  onSave={handleSave}
                />
              </EditorComposer>
            </div>
          </div>
        </div>
      </div>
    </Modal>
    
    {/* 닫기 확인 팝업 */}
    {showCloseConfirm && (
      <Modal
        isOpen={showCloseConfirm}
        onClose={cancelClose}
        title="확인"
        size="small"
        zIndex={9999900}
      >
        <div className="p-4">
          <p className="mb-4">
            수정한 내용이 저장되지 않았습니다.<br />
            정말로 닫으시겠습니까?
          </p>
          <div className="flex justify-end gap-2">
            <Button
              onClick={cancelClose}
              variant="secondary"
              className="px-4 py-2"
            >
              취소
            </Button>
            <Button
              onClick={confirmClose}
              variant="primary"
              className="bg-red-500 hover:bg-red-600 px-4 py-2"
            >
              닫기
            </Button>
          </div>
        </div>
      </Modal>
    )}
    </>
  );
}

export default SolutionEditModal;
